import { useMyUser } from '@/shared/hooks/data-hooks/users/useMyUser'
import { ApiErrorResponse } from '@/types/api'
import { IUser, UserInclude } from '@/types/user'
import React, { createContext, useContext } from 'react'

type UserContextType = {
  user: IUser | null
  isLoading: boolean
  error?: ApiErrorResponse
  refetch: () => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

type UserProviderProps = {
  throwOnError?: boolean
  bailOnLoading?: boolean
  children: React.ReactNode
}

export const UserProvider: React.FC<UserProviderProps> = ({
  children,
  throwOnError,
  bailOnLoading,
}) => {
  const {
    data: { data: user } = {},
    isLoading,
    error,
    refetch,
  } = useMyUser({
    include: [
      UserInclude.Company,
      UserInclude.Profile,
      UserInclude.Workspaces,
      UserInclude.Subscriptions,
    ],
    options: {
      retry: false,
      throwOnError,
    },
  })

  const contextValue: UserContextType = {
    user: user || null,
    isLoading,
    error,
    refetch,
  }

  if (bailOnLoading && isLoading) {
    return null
  }

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  )
}

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider')
  }
  return context
}
